import * as React from 'react';

import Select from 'antd/es/select';

import {
	ItemsProvider,
	useItemsProviderContext,
	SortingDirection,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import ResizeObserverContainer from '@common/react/components/UI/ResizeObserverContainer/ResizeObserverContainer';
import FiltersComponent from '@common/react/components/UI/FiltersComponent/FiltersPropsHandler';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import LoadMoreButton from '@common/react/components/Core/ItemsProvider/LoadMoreButton';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import { useMobileView } from '@common/react/hooks/useMobileView';

import ImageWithSkeleton from '@commonTuna/react/components/UI/ImageWithSkeleton/ImageWithSkeleton';

import { Special } from '@app/objects/Special';
import SpecialCard from '@app/components/Pages/Specials/SpecialCard';
import { Doctor } from '@app/objects/Doctor';
import MobileFilters from '@app/components/UI/MobileFilters/MobileFilters';
import { getLocationName } from '@app/utils/Utils';
import Filter from '@app/components/UI/Filter/Filter';

import sortingDefault from '@app/images/sort-default.png';
import sorting from '@app/images/sort.png';

import '@app/scss/components/special.scss';

const { Option } = Select;

export const SortingDirectionNames = {
	[SortingDirection.Default]: 'Default',
	[SortingDirection.Ascending]: 'Cheap first',
	[SortingDirection.Descending]: 'Expensive first',
};

const SpecialLoader: React.FC = () => {
	const items = [1, 2, 3, 4, 5, 6];
	return <div className="special-list mb10">
		{items.map((i) => <div className="special-card special-loader" key={i} />)}
	</div>;
};

const SpecialsInner: React.FC = () => {
	const { state: { items, loading } } = useItemsProviderContext<Special>();

	if (loading) {
		return <SpecialLoader />;
	}

	const isEmpty = items.length === 0;
	const emptyClassName = isEmpty ? 'empty-list' : '';

	return <div className={`special-list ${emptyClassName}`}>
		{isEmpty
			? <h1 className="text-center">Sorry, there are no special offers at the moment, please check back later.</h1>
			: items.map((special) => <SpecialCard key={special.id} item={special} />)
		}
	</div>;
};

const pagination = { pageSize: 5, current: 1 };

const filtersForClear = [
	'regionIds',
	'locationIds',
	'doctorIds',
];

const moreFiltersWidth = 112;

const SpecialsFilters: React.FC<{page, isMobile}> = ({ page, isMobile }) => {
	const { state: { filters }, actions: { handleChange } } = useItemsProviderContext<Special>();
	const [count, setCount] = React.useState(3);

	const list = [
		<Filter
			data-width="112"
			isMobile={isMobile}
			key="Procedures"
			title="Procedures"
			type="globalProcedureListRemote"
			loadRequest="globalProcedureListRemote"
			values={filters.globalProcedureIds}
			onChange={(value) => handleChange({ globalProcedureIds: !value?.length ? undefined : value })}
		/>,
		<Filter
			data-width="81"
			isMobile={isMobile}
			key="Clinics"
			title="Clinics"
			loadRequest="clinicListForSpecialsPage"
			type="clinicListForSpecialsPage"
			values={filters.locationIds || []}
			pagination={pagination}
			renderName={(option) => `${option.company ? `${option.company.portalShortName || option.company.name} - ` : ''}${option.nameEn}`}
			getOptionProps={(option: any) => {
				return {
					key: option.id,
					title: `${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`,
					value: `${option.company ? `${option.company.portalShortName || option.company.name} - ` : ''}${option.nameEn}`,
					item: option,
					children: <>
						{option.avatar
							&& <div className="option-img__container">
								<ImageWithSkeleton className="option-img" src={`/remote/${option.avatar}`} />
							</div>
						}
						{getLocationName(option, undefined, true)}
					</>,
				};
			}}
			onChange={(value) => handleChange({ locationIds: !value?.length ? undefined : value })}
			// initItems={page?.filterProps?.clinics}
			// initLoadedAll={page?.filterProps?.clinics?.length > 5}
		/>,
		<Filter
			data-width="90"
			isMobile={isMobile}
			key="Doctors"
			titleClassName="filter-title"
			initItems={page?.filterProps?.doctors}
			initLoadedAll={page?.filterProps?.doctors?.length > 5}
			pagination={pagination}
			title="Providers"
			loadRequest="doctorListForSpecialsPage"
			type="doctorListForSpecialsPage"
			values={filters.doctorIds || []}
			onChange={(value) => handleChange({ doctorIds: !value?.length ? undefined : value })}
			renderName={(item: Doctor) => item.nameEn}
		/>,
		<Filter
			data-width="101"
			isMobile={isMobile}
			key="Locations"
			title="Locations"
			loadRequest="regionList"
			titleClassName="filter-title"
			type="regionList"
			values={filters.regionIds || []}
			onChange={(value) => handleChange({ regionIds: !value?.length ? undefined : value })}
			initLoadedAll={page?.filterProps?.locations?.length < 5}
			initItems={page?.filterProps?.locations}
			pagination={pagination}
		/>,
		/* <Filter
			data-width="111"
			isMobile={isMobile}
			key="Specialties"
			title="Specialties"
			loadRequest="specialtyPortalPageViewListRemote"
			titleClassName="filter-title"
			type="specialtyPortalPageViewListRemote"
			values={filters.regionIds || []}
			onChange={(value) => handleChange({ specialties: !value?.length ? undefined : value })}
			initLoadedAll={page?.filterProps?.specialties?.length < 5}
			initItems={page?.filterProps?.specialties}
			pagination={pagination}
		/>, */
	];

	return <ResizeObserverContainer
		onResize={({ width }) => {
			let count = 0;
			let currentWidth = width - 360;
			for (let i = 0; i < list.length; i++) {
				const newCurrentWidth = currentWidth - +(list[i].props['data-width'] || 0) - 10;
				if (newCurrentWidth <= moreFiltersWidth && i + 1 !== list.length) {
					break;
				}

				if (newCurrentWidth > 0) {
					count++;
					currentWidth -= +(list[i].props['data-width'] || 0) + 10;
				}
			}
			if (count > list.length) count = list.length;
			if (count < 0) count = 0;
			if (window.innerWidth <= 768) count = 0;
			setCount(count);
		}}
		className="filters-container list-filters clearfix"
	>
		<div className="pull-left">
			<SimpleSearchInput />
		</div>
		{list.slice(0, count)}
		<Select
			key="Sorting mobile"
			placeholder="Sorting"
			className="mb10 pull-left"
			onChange={(value) => {
				handleChange({
					currentTarget: {
						name: 'column',
						value: [
							{ caption: 'name', direction: SortingDirection.Ascending },
							{ caption: 'price', direction: value },
						],
					},
				});
			}}
			style={{ width: 46, marginLeft: 10 }}
			defaultValue={SortingDirection.Default}
			dropdownStyle={{
				minWidth: 160,
			}}
			value={+(filters.column?.find((c) => c.caption === 'price')?.direction ?? SortingDirection.Ascending)}
		>
			<Option value={SortingDirection.Default}>
				<img className="sorting-icon" src={sortingDefault} alt="" />
				<span className="hidden-item">
					{SortingDirectionNames[SortingDirection.Default]}
				</span>
			</Option>
			<Option value={SortingDirection.Ascending}>
				<img className="sorting-icon icon-inverse" src={sorting} alt="" />
				<span className="hidden-item">
					{SortingDirectionNames[SortingDirection.Ascending]}
				</span>
			</Option>
			<Option value={SortingDirection.Descending}>
				<img className="sorting-icon" src={sorting} alt="" />
				<span className="hidden-item">
					{SortingDirectionNames[SortingDirection.Descending]}
				</span>
			</Option>
		</Select>
		{count !== list.length ? <div className="pull-left ml10" style={{ width: 'max-content' }}>
			<FiltersComponent
				apply
				additionalParams={filters}
				popoverStyle={{ minWidth: '300px' }}
				getPopupContainer={(node) => node.closest('.specials-container') || document.body}
				popoverClassName="doctor-filters-popover"
				buttonClassName="pull-right ml10"
				filtersClean={() => {
					const params = {};
					Object.values(filtersForClear.slice(count))
						.forEach((key) => params[key] = undefined);
					handleChange({ ...params });
				}}
				triggerNode={isMobile ? undefined
					: <button type="button" className="more-filters__trigger btn btn-default">
						More Filters
					</button>}
				placement={isMobile ? undefined : null}
				getChildrenList={(tooltipValues) => tooltipValues}
				tooltipValues={
					<div
						className="filters-container clearfix filters-wrapper filters-component-title__container"
						style={{ margin: 0 }}
						data-param={filtersForClear.slice(count)}
					>
						{list.slice(count)}
					</div>}
				customNode={isMobile ? MobileFilters : undefined}
			/>
		</div> : null}
	</ResizeObserverContainer>;
};

const Specials: React.FC = () => {
	const isMobile = useMobileView(768);

	return <>
		<ServerPageProvider
			path="specials"
			defaultTitle="Specials"
			inner={(page) => (
				<ItemsProvider<Special>
					rows={page?.items}
					loadRequest="specialsList"
					filters={{
						column: [
							{ caption: 'name', direction: SortingDirection.Ascending },
							{ caption: 'price', direction: SortingDirection.Default }],
					}}
					paginationUrl={{ defaultPageSize: 12 }}
					withHandleUrl
				>
					<div className="container specials-container">
						<div className="specials-filters container">
							<SpecialsFilters isMobile={isMobile} page={page} />
						</div>
						<div className="special-list__wrapper">
							<BreadCrumbs
								title="Specials"
							/>
							<SpecialsInner />
							<div className="text-center load-more-btn mb10">
								<LoadMoreButton
									silent
								>
									Load More
								</LoadMoreButton>
							</div>
						</div>
					</div>
				</ItemsProvider>
			)}
		/>
	</>;
};

export default Specials;
